/**
 * A record's *Email* and *Text* send — on every record page (Aaron, Oct 23:
 * "Sending emails through the app like this is how i want the email function
 * in activities to work on every module in the rowted app").
 *
 * The composer's Email and Text open Activity's own New email and New text
 * (`docs/activity.md` §9.3, §9.4), so there is one sender: the contact's
 * conversation, Rowted's email with the private reply address or the
 * person's Outlook, the same From line and the same attachments. The record
 * comes along as the sheet's chips and as `origin`, so the message is filed
 * on the record it was written from.
 *
 * Aaron, Oct 24: "yes — and, people with inboxes need the option to log over
 * send". So Email and Text are **only for somebody with an Inbox** — whoever
 * has none does not see them at all — and for them each is a choice:
 * **Send** one from Rowted, or **Log** one that happened somewhere else, which
 * is the composer's old *Log email* and *Log text* (an activity on the record;
 * nothing is sent and nothing is queued).
 *
 * Generic on purpose: what a record starts the sheet with, and whether this
 * person may send at all, is the server's
 * `GET /conversations/compose-for/:type/:id`, one table for every kind of
 * record, so a module added later needs nothing on its page.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { ACTIVITY_KINDS, type ActivityKind, type RecordType } from '@rowted/shared'
import { request } from '../lib/api.js'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'
import { Popover } from '../components/overlays.js'
import { PanelSheet } from '../components/panel-sheet.js'
import { IconEdit, IconSend } from '../frame/icons.js'
import { readPrefill, type NewPrefill } from '../activity/new/common.js'
import { EmailSheet } from '../activity/new/email-sheet.js'
import { TextSheet } from '../activity/new/text-sheet.js'

export interface MessageRecord {
  object: RecordType
  id: string
}

interface ComposeFor {
  links: { type: string; id: string; label: string }[]
  contactId: string | null
  canSend: boolean
}

type Channel = 'email' | 'sms'

const SENT_KINDS: readonly ActivityKind[] = ['email', 'sms']
const isSent = (kind: ActivityKind): kind is Channel => SENT_KINDS.includes(kind)

/** Nobody may send: no Inbox, no answer, or no record to send from. */
const NOBODY: ComposeFor = { links: [], contactId: null, canSend: false }

/*
 * One question per record for a short while, however many composers ask it —
 * the busy page's Activity tab, the quiet panel, its phone sheet and the
 * contact's *Send a message* step are four askers on one page. Short-lived,
 * because who a record writes to changes when somebody links a property.
 */
const ASKED_MS = 30_000
const asked = new Map<string, { at: number; answer: Promise<ComposeFor> }>()

function composeFor(record: MessageRecord): Promise<ComposeFor> {
  const key = `${record.object}:${record.id}`
  const known = asked.get(key)
  if (known && Date.now() - known.at < ASKED_MS) return known.answer
  /* A record that cannot be asked about (no access, not an office-app user) offers neither. */
  const answer = request<ComposeFor>(`/conversations/compose-for/${record.object}/${record.id}`).catch(() => NOBODY)
  asked.set(key, { at: Date.now(), answer })
  return answer
}

/** For tests: forget every answer. */
export function forgetComposeFor(): void {
  asked.clear()
}

/**
 * Whether this person may send texts and email from a record — an Inbox
 * (§5.3). `null` while the server is asked; `true` without a record, where
 * the composer only ever logs, so there is nothing to take away.
 */
export function useCanMessage(record: MessageRecord | undefined): boolean | null {
  const object = record?.object
  const id = record?.id
  const [answer, setAnswer] = useState<{ key: string; canSend: boolean } | null>(null)
  useEffect(() => {
    if (!object || !id) return
    let live = true
    void composeFor({ object, id }).then((found) => {
      if (live) setAnswer({ key: `${object}:${id}`, canSend: found.canSend })
    })
    return () => { live = false }
  }, [object, id])
  if (!object || !id) return true
  return answer?.key === `${object}:${id}` ? answer.canSend : null
}

/**
 * The composer's kinds for this person: Email and Text only with an Inbox
 * (Aaron, Oct 24), and not until the server has said so — a button that
 * appears and then vanishes is one somebody pressed in between.
 */
export function messageKinds(canSend: boolean | null): readonly ActivityKind[] {
  return canSend === true ? ACTIVITY_KINDS : ACTIVITY_KINDS.filter((kind) => !isSent(kind))
}

/** Where a choice was asked from: the composer's button, or a sheet that draws the rows itself. */
interface Choosing { channel: Channel; anchor: HTMLElement | null; log: () => void }

export interface RecordMessage {
  /** Which of the composer's six kinds this person is offered. */
  kinds: readonly ActivityKind[]
  /** Whether this person may send (an Inbox); `null` while asked. */
  canSend: boolean | null
  /**
   * The composer picked a kind. True when it is Email or Text on a record:
   * the choice opens — *Send* (Activity's New email or New text) or *Log*,
   * which calls `log` so the composer logs one as it always did. False for
   * everything else: the composer carries on. `anchor` is the button the
   * choice hangs off; without one (`inline`) the caller draws the rows.
   */
  take: (kind: ActivityKind, log: () => void, anchor?: HTMLElement | null) => boolean
  /** Opens Activity's New email or New text for this record. */
  send: (channel: Channel) => void
  /** The open choice and sheet, or nothing. Render it anywhere: both are portals. */
  sheet: ReactNode
}

export function useRecordMessage(record: MessageRecord | undefined, onSent?: () => void): RecordMessage {
  const phone = usePhone()
  const canSend = useCanMessage(record)
  const [open, setOpen] = useState<{ channel: Channel; prefill: NewPrefill } | null>(null)
  const [choosing, setChoosing] = useState<Choosing | null>(null)
  const recordRef = useRef(record)
  recordRef.current = record

  const send = useCallback((channel: Channel) => {
    const here = recordRef.current
    setChoosing(null)
    if (!here) return
    void composeFor(here).then((answer) => {
      if (!answer.canSend) return
      setOpen({
        channel,
        prefill: readPrefill({
          ...(answer.contactId ? { contactId: answer.contactId } : {}),
          links: answer.links,
          origin: { type: here.object, id: here.id },
        }),
      })
    })
  }, [])

  const take = useCallback((kind: ActivityKind, log: () => void, anchor?: HTMLElement | null): boolean => {
    if (!record || !isSent(kind)) return false
    void composeFor(record).then((answer) => {
      /* Nobody without an Inbox sees Email or Text; a step that aimed at one anyway logs nothing either. */
      if (!answer.canSend) return
      setChoosing({ channel: kind, anchor: anchor ?? null, log })
    })
    return true
  }, [record])

  const close = useCallback(() => setOpen(null), [])
  const made = useCallback(() => { onSent?.() }, [onSent])

  const choice = choosing && (
    <MessageChoice
      channel={choosing.channel}
      anchor={choosing.anchor}
      phone={phone}
      onSend={() => send(choosing.channel)}
      onLog={() => { setChoosing(null); choosing.log() }}
      onClose={() => setChoosing(null)}
    />
  )

  const sheet = open === null ? choice
    : open.channel === 'email'
      ? <EmailSheet prefill={open.prefill} phone={phone} onClose={close} onMade={made} />
      : (
        <TextSheet prefill={open.prefill} phone={phone} onClose={close} onMade={made}
          onSwitch={(_next, from) => setOpen({ channel: 'email', prefill: from })} />
      )

  return { kinds: messageKinds(canSend), canSend, take, send, sheet }
}

/** *Send an email* · *Log an email* — the words of the two rows, one place. */
export function choiceWords(channel: Channel): { title: string; send: string; log: string } {
  return channel === 'email'
    ? { title: 'Email', send: 'Send an email', log: 'Log an email' }
    : { title: 'Text', send: 'Send a text', log: 'Log a text' }
}

/**
 * The two answers, as rows: the same body in a popover off the composer's
 * button and in a phone's sheet, so the words cannot drift apart.
 */
export function MessageChoiceRows({ channel, phone, onSend, onLog }: {
  channel: Channel
  phone: boolean
  onSend: () => void
  onLog: () => void
}) {
  const words = choiceWords(channel)
  const row = (label: string, sub: string, icon: ReactNode, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className={cn('flex w-full items-center gap-2.5 px-3.5 text-left hover:bg-hover',
        phone ? 'min-h-[60px] py-2.5' : 'min-h-[52px] py-2')}
    >
      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-button bg-accent-soft text-accent-dark">
        {icon}
      </span>
      <span className="flex min-w-0 flex-col">
        <b className="text-body font-semibold text-text">{label}</b>
        <span className="text-sub text-text-muted">{sub}</span>
      </span>
    </button>
  )
  return (
    <div className="flex flex-col py-1">
      {row(words.send, 'From Rowted — replies come back to your Inbox', <IconSend size={15} />, onSend)}
      {row(words.log, 'One that happened somewhere else — nothing is sent', <IconEdit size={15} />, onLog)}
    </div>
  )
}

/** The choice: a popover under the composer's button, a bottom sheet on a phone (§6.1). */
function MessageChoice({ channel, anchor, phone, onSend, onLog, onClose }: {
  channel: Channel
  anchor: HTMLElement | null
  phone: boolean
  onSend: () => void
  onLog: () => void
  onClose: () => void
}) {
  const words = choiceWords(channel)
  const rows = <MessageChoiceRows channel={channel} phone={phone} onSend={onSend} onLog={onLog} />
  if (phone || !anchor) {
    return <PanelSheet open onClose={onClose} title={words.title}>{rows}</PanelSheet>
  }
  return (
    <Popover open onClose={onClose} anchor={anchor} width={320} label={words.title} className="rounded-xl">
      {rows}
    </Popover>
  )
}
